// react-query
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// vercel analytics
import { Analytics } from "@vercel/analytics/react";
import { Provider } from "react-redux";
// react
import { Suspense } from "react";
import ReactDOM from "react-dom/client";
// helmet
import { HelmetProvider } from "react-helmet-async";
// svg icons
import "virtual:svg-icons-register";
// mock api
import worker from "./_mock";
// i18n
import "./locales/i18n";
// css
import "./global.css";
import "./theme/theme.css";

// root component
import App from "./App";
import ProgressBar from "./components/progress-bar";
import { store } from "./store/store";
import { AuthProvider } from "./contexts/AuthContext";
import axios from "axios";
import useUserStore from "./store/userStore";
import { toast } from "sonner";

const AI_ENDPOINTS = [
	'/api/v1/observations',
	'/api/v1/recommend',
	'/api/v1/analyze-multi-metadata',
	'/api/v1/new-dashboard-plan',
	'/api/v1/execution-new',
	'/api/v1/dashboard-storyline',
	'/api/v1/dashboard-chart-reasoning',
	'/api/v1/validate-custom-question',
	'/api/v1/validate-custom-objective',
	'/api/v1/suggest-questions-list'
];

axios.interceptors.request.use((config) => {
	const state = useUserStore.getState();
	const localUserId = localStorage.getItem('userId');
	const userId = state.userInfo?.user_id || state.userInfo?.id || localUserId || state.userInfo?.email || 'test_user_123';

	// Inject x-user-id header into AI API requests for credit tracking
	if (config.url && AI_ENDPOINTS.some(ep => config.url?.includes(ep))) {
		if (config.headers) {
			if (typeof config.headers.set === 'function') {
				config.headers.set('x-user-id', String(userId));
			} else {
				config.headers['x-user-id'] = String(userId);
			}
		} else {
			config.headers = { 'x-user-id': String(userId) } as any;
		}

		const credits = state.creditsRemaining;
		if (credits !== null && credits <= 0) {
			toast.error("Credits Exhausted. Please upgrade to continue using Vantage AI.");
			return Promise.reject(new Error("Credits Exhausted"));
		}
	}
	return config;
});

axios.interceptors.response.use((response) => {
	if (response.config.url && AI_ENDPOINTS.some(ep => response.config.url?.includes(ep))) {
		useUserStore.getState().actions.refreshCredits();
	}
	return response;
}, (error) => {
	if (error.response?.status === 429 && typeof error.response?.data?.detail === 'string' && error.response.data.detail.includes("Insufficient credits")) {
		useUserStore.getState().actions.setInsufficientModalOpen(true);
		useUserStore.getState().actions.setHasInsufficientCredits(true);
	}
	return Promise.reject(error);
});

const charAt = `
    ███████╗██╗      █████╗ ███████╗██╗  ██╗ 
    ██╔════╝██║     ██╔══██╗██╔════╝██║  ██║
    ███████╗██║     ███████║███████╗███████║
    ╚════██║██║     ██╔══██║╚════██║██╔══██║
    ███████║███████╗██║  ██║███████║██║  ██║
    ╚══════╝╚══════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝
  `;
console.info(`%c${charAt}`, "color: #5BE49B");

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

root.render(
	<Provider store={store}>
		<AuthProvider>
			<HelmetProvider>
				<QueryClientProvider client={new QueryClient()}>
					{/* <ReactQueryDevtools initialIsOpen={false} /> */}
					<Suspense>
						<ProgressBar />
						<Analytics />
						<App />
					</Suspense>
				</QueryClientProvider>
			</HelmetProvider></AuthProvider>
	</Provider>,
);

// 🥵 start service worker mock in development mode
const useMock = import.meta.env.VITE_USE_MOCK === 'true';
if (useMock) {
  worker.start({ onUnhandledRequest: "bypass" });
} else {
  console.log("MSW (Mock API) is disabled. Set VITE_USE_MOCK=true in .env to enable.");
}
